import {
  CaptureUpdateAction,
  Excalidraw,
  ExcalidrawAPIProvider,
  Footer,
  MainMenu,
} from "@excalidraw/excalidraw";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { convertToExcalidrawElements } from "@excalidraw/element";
import { exportToBlob, exportToSvg } from "@excalidraw/utils/export";
import { debounce } from "@excalidraw/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  blobToDataURL,
  createEmbedEvent,
  isEmbedHostMessage,
  parseEmbedOptions,
  parseInitialJson,
  serializeEmbedScene,
} from "./embedProtocol";
import { shouldEnableEmbedProtocol } from "./entrypoint";

import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type {
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type { ExcalidrawElementSkeleton } from "@excalidraw/element";
import type { EmbedHostMessage, EmbedLangCode } from "./embedProtocol";

import { useHandleAppTheme } from "./useHandleAppTheme";

import "./index.scss";
import "./SimpleApp.scss";

const SIMPLE_SCENE_STORAGE_KEY = "excalidraw-simple-scene";
const SIMPLE_SCENE_SAVE_TIMEOUT = 500;

type StoredSimpleScene = Pick<
  Required<ExcalidrawInitialDataState>,
  "appState" | "elements" | "files"
>;

type EmbedSceneData = {
  elements: readonly OrderedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
};

type EmbedElementInput = Record<string, any>;

const emptyScene = (): EmbedSceneData => ({
  elements: [],
  appState: {} as AppState,
  files: {},
});

const postToParent = (event: ReturnType<typeof createEmbedEvent>) => {
  window.parent.postMessage(event, "*");
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const cleanNumber = (value: unknown, fallback = 0) =>
  isFiniteNumber(value) ? value : fallback;

const hasOwn = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

const cleanPoint = (point: unknown): [number, number] | null => {
  if (!Array.isArray(point) || point.length < 2) {
    return null;
  }

  return [cleanNumber(point[0], 0), cleanNumber(point[1], 0)];
};

const cleanLinearPoints = (element: EmbedElementInput) => {
  const points = Array.isArray(element.points)
    ? element.points.map(cleanPoint).filter(Boolean)
    : [];

  return points.length >= 2
    ? points
    : [
        [0, 0],
        [cleanNumber(element.width, 0), cleanNumber(element.height, 0)],
      ];
};

const fixZeroDimensionLinearElement = (element: EmbedElementInput) => {
  if (element.type !== "arrow" && element.type !== "line") {
    return element;
  }

  if (element.width === 0) {
    element.width = 1;
  }
  if (element.height === 0) {
    element.height = 1;
  }

  return element;
};

const cleanBinding = (binding: unknown) => {
  if (!binding || typeof binding !== "object") {
    return null;
  }

  const elementId = (binding as { elementId?: unknown; id?: unknown }).elementId ??
    (binding as { elementId?: unknown; id?: unknown }).id;

  return typeof elementId === "string" && elementId
    ? { elementId, focus: 0, gap: 0 }
    : null;
};

const cleanBoundElements = (boundElements: unknown) =>
  Array.isArray(boundElements)
    ? boundElements
        .map((binding) => {
          if (!binding || typeof binding !== "object") {
            return null;
          }
          const id = (binding as { id?: unknown }).id;
          const type = (binding as { type?: unknown }).type;
          return typeof id === "string" && id
            ? { id, type: type === "text" ? "text" : "arrow" }
            : null;
        })
        .filter(Boolean)
    : null;

const cleanEmbedElements = (
  elements: ExcalidrawInitialDataState["elements"],
): EmbedElementInput[] => {
  if (!Array.isArray(elements)) {
    return [];
  }

  return elements
    .filter((element): element is EmbedElementInput =>
      !!element && typeof element === "object" && typeof element.type === "string",
    )
    .map((element) => {
      const cleaned: EmbedElementInput = {
        ...element,
        x: cleanNumber(element.x, 0),
        y: cleanNumber(element.y, 0),
        angle: cleanNumber(element.angle, 0),
        opacity: cleanNumber(element.opacity, 100),
        groupIds: Array.isArray(element.groupIds) ? element.groupIds : [],
        frameId: typeof element.frameId === "string" ? element.frameId : null,
      };

      if (hasOwn(element, "width")) {
        cleaned.width = cleanNumber(element.width, 0);
      }
      if (hasOwn(element, "height")) {
        cleaned.height = cleanNumber(element.height, 0);
      }
      if (hasOwn(element, "boundElements")) {
        cleaned.boundElements = cleanBoundElements(element.boundElements);
      }

      if (cleaned.type === "arrow" || cleaned.type === "line") {
        fixZeroDimensionLinearElement(cleaned);

        if (Array.isArray(cleaned.points)) {
          cleaned.points = cleaned.points.map(cleanPoint).filter(Boolean);
        }
        if (hasOwn(cleaned, "startBinding")) {
          cleaned.startBinding = cleanBinding(cleaned.startBinding);
        }
        if (hasOwn(cleaned, "endBinding")) {
          cleaned.endBinding = cleanBinding(cleaned.endBinding);
        }
      }

      if (cleaned.type === "freedraw") {
        cleaned.points = Array.isArray(cleaned.points)
          ? cleaned.points.map(cleanPoint).filter(Boolean)
          : [];
        cleaned.pressures = Array.isArray(cleaned.pressures) ? cleaned.pressures : [];
      }

      if (cleaned.type === "text") {
        cleaned.text = typeof cleaned.text === "string" ? cleaned.text : "";
        cleaned.originalText = typeof cleaned.originalText === "string"
          ? cleaned.originalText
          : cleaned.text;
      }

      return cleaned;
    });
};

const isCompleteEmbedElement = (element: EmbedElementInput) =>
  isFiniteNumber(element.version) &&
  isFiniteNumber(element.versionNonce) &&
  isFiniteNumber(element.seed);

const isSkeletonEmbedScene = (elements: readonly EmbedElementInput[]) =>
  elements.some((element) => (
    hasOwn(element, "label") ||
    hasOwn(element, "start") ||
    hasOwn(element, "end") ||
    !isCompleteEmbedElement(element)
  ));

const getRestorableEmbedElements = (
  elements: EmbedElementInput[],
): readonly OrderedExcalidrawElement[] => {
  if (isSkeletonEmbedScene(elements)) {
    return convertToExcalidrawElements(
      elements as ExcalidrawElementSkeleton[],
      { regenerateIds: false },
    ) as readonly OrderedExcalidrawElement[];
  }

  const restorableElements = elements.map((element) => {
    if (element.type === "arrow" || element.type === "line") {
      return {
        ...element,
        points: Array.isArray(element.points)
          ? element.points
          : cleanLinearPoints(element),
      };
    }

    return element;
  });

  return restorableElements as unknown as readonly OrderedExcalidrawElement[];
};

const restoreEmbedScene = (
  scene: ExcalidrawInitialDataState | null,
): ExcalidrawInitialDataState | null => {
  if (!scene) {
    return null;
  }

  const elements = getRestorableEmbedElements(cleanEmbedElements(scene.elements));

  return {
    ...scene,
    elements: restoreElements(elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
  };
};

const loadInitialData = (): ExcalidrawInitialDataState | null => {
  try {
    const savedScene = localStorage.getItem(SIMPLE_SCENE_STORAGE_KEY);

    if (!savedScene) {
      return null;
    }

    const data = JSON.parse(savedScene) as StoredSimpleScene;

    return {
      appState: data.appState,
      elements: data.elements,
      files: data.files,
      scrollToContent: true,
    };
  } catch (error) {
    console.warn("Failed to restore simple editor scene", error);
    return null;
  }
};

const saveSceneToLocalStorage = debounce(
  (
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    try {
      const data: StoredSimpleScene = {
        elements,
        appState: clearAppStateForLocalStorage(appState),
        files,
      };

      localStorage.setItem(SIMPLE_SCENE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to save simple editor scene", error);
    }
  },
  SIMPLE_SCENE_SAVE_TIMEOUT,
);

const SimpleMainMenu = ({
  appTheme,
  setAppTheme,
}: Pick<ReturnType<typeof useHandleAppTheme>, "appTheme" | "setAppTheme">) => (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences>
        <MainMenu.DefaultItems.Preferences.ToggleGridMode />
        <MainMenu.DefaultItems.Preferences.ToggleToolLock />
        <MainMenu.DefaultItems.Preferences.ToggleSnapMode />
        <MainMenu.DefaultItems.Preferences.ToggleArrowBinding />
        <MainMenu.DefaultItems.Preferences.ToggleMidpointSnapping />
      </MainMenu.DefaultItems.Preferences>
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={appTheme}
        onSelect={setAppTheme}
      />
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
);

const SimpleAppContent = () => {
  const embedEnabled = useMemo(
    () => shouldEnableEmbedProtocol(new URL(window.location.href)),
    [],
  );
  const embedOptions = useMemo(
    () => parseEmbedOptions(new URL(window.location.href)),
    [],
  );
  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();
  const [langCode, setLangCode] = useState<EmbedLangCode>(
    embedOptions.langCode ?? "en",
  );
  const [embedTheme, setEmbedTheme] = useState(embedOptions.theme);
  const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const sceneDataRef = useRef<EmbedSceneData>(emptyScene());
  const initialDataRef = useRef<ExcalidrawInitialDataState | null>(
    embedOptions.initialJson
      ? restoreEmbedScene(embedOptions.initialJson)
      : embedEnabled
      ? null
      : loadInitialData(),
  );

  const activeTheme = embedEnabled && embedTheme ? embedTheme : editorTheme;
  const viewModeEnabled = embedEnabled
    ? embedOptions.viewModeEnabled ?? false
    : undefined;
  const gridModeEnabled = embedEnabled
    ? embedOptions.gridModeEnabled ?? false
    : undefined;

  const getJson = useCallback(
    () =>
      serializeEmbedScene(
        sceneDataRef.current.elements,
        sceneDataRef.current.appState,
        sceneDataRef.current.files,
      ),
    [],
  );

  const exportPngBlob = useCallback(
    () =>
      exportToBlob({
        elements: sceneDataRef.current.elements as any,
        appState: sceneDataRef.current.appState,
        files: sceneDataRef.current.files,
        mimeType: "image/png",
      }),
    [],
  );

  const exportSvgElement = useCallback(
    () =>
      exportToSvg({
        elements: sceneDataRef.current.elements as any,
        appState: sceneDataRef.current.appState,
        files: sceneDataRef.current.files,
      }),
    [],
  );

  const respond = useCallback((requestId: string | undefined, payload: any) => {
    postToParent(createEmbedEvent("response", payload, requestId));
  }, []);

  const reportError = useCallback(
    (requestId: string | undefined, error: unknown) => {
      postToParent(
        createEmbedEvent(
          "error",
          {
            message:
              error instanceof Error ? error.message : "Unknown embed error",
          },
          requestId,
        ),
      );
    },
    [],
  );

  const loadScene = useCallback((scene: ExcalidrawInitialDataState | null) => {
    const api = excalidrawAPIRef.current;
    if (!api) {
      return;
    }

    const files = scene?.files ?? {};
    const fileValues = Object.values(files);
    if (fileValues.length) {
      api.addFiles(fileValues);
    }

    const restoredScene = restoreEmbedScene(scene);

    api.updateScene({
      elements: restoredScene?.elements ?? [],
      appState: restoreAppState(scene?.appState ?? null, null),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }, []);

  const openNativeExportDialog = useCallback(
    (dialogName: "imageExport" | "jsonExport") => {
      const api = excalidrawAPIRef.current;
      if (!api) {
        throw new Error("Excalidraw API is not ready");
      }

      api.updateScene({
        appState: {
          openDialog: { name: dialogName },
        },
      });
    },
    [],
  );

  const handleChange = useCallback<NonNullable<ExcalidrawProps["onChange"]>>(
    (elements, appState, files) => {
      sceneDataRef.current = {
        elements,
        appState,
        files,
      };

      if (embedEnabled) {
        postToParent(
          createEmbedEvent("change", {
            json: serializeEmbedScene(elements, appState, files),
            elements,
            appState,
            files,
          }),
        );
        return;
      }

      saveSceneToLocalStorage(elements, appState, files);
    },
    [embedEnabled],
  );

  const handleExport = useCallback<NonNullable<ExcalidrawProps["onExport"]>>(
    async (_type, data, { signal }) => {
      const pendingImages = data.elements.filter(
        (element) => element.type === "image" && element.status === "pending",
      );

      if (!pendingImages.length) {
        return;
      }

      await new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(resolve, 1000);
        signal.addEventListener(
          "abort",
          () => {
            window.clearTimeout(timeoutId);
            reject(signal.reason);
          },
          { once: true },
        );
      });
    },
    [],
  );

  const uiOptions = useMemo<ExcalidrawProps["UIOptions"]>(
    () => ({
      canvasActions: {
        changeViewBackgroundColor: true,
        clearCanvas: true,
        export: { saveFileToDisk: true },
        loadScene: true,
        saveToActiveFile: true,
        saveAsImage: true,
        toggleTheme: null,
      },
      tools: {
        image: true,
      },
    }),
    [],
  );

  const handleEmbedMessage = useCallback(
    async (message: EmbedHostMessage) => {
      try {
        if (message.type === "load") {
          loadScene(parseInitialJson(message.payload));
          respond(message.requestId, { ok: true });
          return;
        }

        if (message.type === "getJson") {
          respond(message.requestId, { json: getJson() });
          return;
        }

        if (message.type === "exportPng") {
          const blob = await exportPngBlob();
          respond(message.requestId, { dataURL: await blobToDataURL(blob) });
          return;
        }

        if (message.type === "exportSvg") {
          const svg = await exportSvgElement();
          respond(message.requestId, { svg: svg.outerHTML });
          return;
        }

        if (message.type === "downloadPng") {
          openNativeExportDialog("imageExport");
          respond(message.requestId, { ok: true });
          return;
        }

        if (message.type === "downloadSvg") {
          openNativeExportDialog("imageExport");
          respond(message.requestId, { ok: true });
          return;
        }

        if (message.type === "downloadSource") {
          openNativeExportDialog("jsonExport");
          respond(message.requestId, { ok: true });
          return;
        }

        if (message.type === "setLang") {
          if (message.payload === "en" || message.payload === "zh-CN") {
            setLangCode(message.payload);
          }
          respond(message.requestId, { ok: true });
          return;
        }

        if (message.type === "setTheme") {
          if (message.payload === "light" || message.payload === "dark") {
            setEmbedTheme(message.payload);
          }
          respond(message.requestId, { ok: true });
        }
      } catch (error) {
        reportError(message.requestId, error);
      }
    },
    [
      exportPngBlob,
      exportSvgElement,
      getJson,
      loadScene,
      openNativeExportDialog,
      reportError,
      respond,
    ],
  );

  useEffect(() => {
    if (!embedEnabled) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (isEmbedHostMessage(event.data)) {
        handleEmbedMessage(event.data);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedEnabled, handleEmbedMessage]);

  const handleRawApi = useCallback((api: ExcalidrawImperativeAPI | null) => {
    excalidrawAPIRef.current = api;
    if (!api || !embedEnabled) {
      return;
    }

    sceneDataRef.current = {
      elements: api.getSceneElements() as readonly OrderedExcalidrawElement[],
      appState: api.getAppState(),
      files: api.getFiles(),
    };
    postToParent(
      createEmbedEvent("ready", {
        version: 1,
        capabilities: [
          "load",
          "getJson",
          "exportPng",
          "exportSvg",
          "downloadPng",
          "downloadSvg",
          "downloadSource",
          "setLang",
          "setTheme",
        ],
      }),
    );
  }, [embedEnabled]);

  return (
    <div className="simple-excalidraw-app" data-testid="simple-app-root">
      <Excalidraw
        initialData={initialDataRef.current}
        onChange={handleChange}
        onExport={handleExport}
        onExcalidrawAPI={handleRawApi}
        UIOptions={uiOptions}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        langCode={langCode}
        theme={activeTheme}
        viewModeEnabled={viewModeEnabled}
        gridModeEnabled={gridModeEnabled}
        aiEnabled={false}
      >
        <SimpleMainMenu appTheme={appTheme} setAppTheme={setAppTheme} />
        <Footer />
      </Excalidraw>
    </div>
  );
};

const SimpleApp = () => (
  <ExcalidrawAPIProvider>
    <SimpleAppContent />
  </ExcalidrawAPIProvider>
);

export default SimpleApp;
